import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  subscriptionService,
  type SubscriptionStatus,
} from '@/api/services/notifications';

type Banner = {
  severity: 'success' | 'warning' | 'orange' | 'error';
  title: string;
  body: string;
  actionLabel?: string;
  actionUrl?: string;
  dismissible?: boolean;
};

function buildBanner(status: SubscriptionStatus): Banner | null {
  const state = status.lifecycleState;
  if (state === 'renewed') {
    return {
      severity: 'success',
      title: 'Subscription renewed',
      body: 'Subscription renewed successfully.',
      dismissible: true,
    };
  }
  if (status.paymentStatus === 'failed' || state === 'grace') {
    const grace = status.graceUntil
      ? new Date(status.graceUntil).toLocaleDateString()
      : 'soon';
    return {
      severity: 'orange',
      title: 'Payment failed',
      body: `Payment failed. Retry before ${grace}.`,
      actionLabel: 'Update payment',
      actionUrl: '/usage',
    };
  }
  if (state === 'expired' || state === 'revoked') {
    return {
      severity: 'error',
      title: 'Subscription expired',
      body: 'Your subscription has expired. Searches may be paused until you renew.',
      actionLabel: 'Renew',
      actionUrl: '/usage',
    };
  }
  if (state.startsWith('expiring_')) {
    const days = state.replace('expiring_', '');
    const label = days === '1' ? 'tomorrow' : `in ${days} days`;
    return {
      severity: 'warning',
      title: 'Subscription expiring',
      body: `Your subscription expires ${label}. Renew now to avoid interruption.`,
      actionLabel: 'Renew',
      actionUrl: '/usage',
    };
  }
  return null;
}

export default function DashboardBanner() {
  const [banner, setBanner] = useState<Banner | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    subscriptionService
      .status()
      .then((status) => setBanner(buildBanner(status)))
      .catch(() => setBanner(null));
  }, []);

  if (!banner || dismissed) return null;

  return (
    <div className={`dashboard-banner dashboard-banner--${banner.severity}`}>
      <div>
        <strong>{banner.title}</strong>
        <p className="dashboard-banner__body">{banner.body}</p>
      </div>
      <div className="dashboard-banner__actions">
        {banner.actionLabel && banner.actionUrl ? (
          <Link className="btn btn-sm" to={banner.actionUrl}>
            {banner.actionLabel}
          </Link>
        ) : null}
        {banner.dismissible ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDismissed(true)}>
            Dismiss
          </button>
        ) : null}
      </div>
    </div>
  );
}
